import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Statistic,
  Row,
  Col,
  message,
  Drawer,
  Descriptions,
  Tag,
  Select,
  DatePicker,
  Collapse,
  Empty,
  Spin,
  Popconfirm,
  Pagination
} from 'antd'
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  DollarOutlined,
  ShoppingOutlined,
  FilterOutlined,
  DownOutlined,
  UpOutlined,
  CloseCircleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import {
  getInventoryUsage,
  getInventoryUsageDetail,
  cancelInventoryUsage
} from '../api/inventory'
import { getInventoryProducts } from '../api/inventory'
import api from '../utils/api'
import dayjs from 'dayjs'
import { useMediaQuery } from 'react-responsive'
import { MobileRecordCard } from '../components/common/MobileRecordCard'

const { RangePicker } = DatePicker
const { Panel } = Collapse
const { Option } = Select

// 把金額 round 到 2 位小數後格式化（千分位 + 最多 2 位小數，整數不補小數）。
// 用來解掉「0.03 × 760 = 22.799999...」這類浮點誤差顯示問題。
const formatMoney = (n) => {
  const num = Math.round(Number(n || 0) * 100) / 100
  return num.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ActionButtons = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`

const PrimaryButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  height: 40px;
  padding: 0 20px;
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;

  &:hover {
    background: #6d28d9;
    border-color: #6d28d9;
  }

  @media (max-width: 768px) {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
  }
`

const SearchCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .ant-collapse {
    background: transparent;
    border: none;

    .ant-collapse-item {
      border: none;

      .ant-collapse-header {
        padding: 0 !important;
        font-size: 16px;
        font-weight: 600;
        color: #1a1a1a;

        .ant-collapse-arrow {
          right: 0;
          padding: 0;
        }
      }

      .ant-collapse-content {
        border: none;
        background: transparent;
        padding-top: 16px;
      }
    }
  }
`

const SearchForm = styled.div`
  .search-row {
    margin-bottom: 16px;

    @media (max-width: 768px) {
      margin-bottom: 12px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .search-label {
    display: block;
    margin-bottom: 8px;
    color: #374151;
    font-weight: 500;
    font-size: 14px;
  }

  .ant-picker,
  .ant-input,
  .ant-select {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5e7eb;

    &:hover,
    &:focus {
      border-color: #7c3aed;
    }

    &.ant-picker-focused,
    &.ant-input-focused,
    &.ant-select-focused .ant-select-selector {
      border-color: #7c3aed;
      box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
    }
  }
`

const SummaryCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .ant-statistic-title {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .ant-statistic-content {
    color: #1a1a1a;
    font-size: 24px;
    font-weight: 600;
  }
`

const TableCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;

  .ant-card-body {
    padding: 0;
  }

  .ant-table {
    .ant-table-thead > tr > th {
      background: #f9fafb;
      border-bottom: 2px solid #e5e7eb;
      font-weight: 600;
      color: #374151;
      padding: 16px;
      text-align: center;

      @media (max-width: 768px) {
        padding: 12px 8px;
        font-size: 12px;
      }
    }

    .ant-table-tbody > tr > td {
      padding: 16px;
      text-align: center;
      border-bottom: 1px solid #f3f4f6;

      @media (max-width: 768px) {
        padding: 12px 8px;
        font-size: 12px;
      }
    }

    .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
    }
  }
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 24px;
  }
`

const DetailSection = styled.div`
  margin-bottom: 24px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;
  }
`

const ItemList = styled.div`
  display: grid;
  gap: 12px;
`

const ItemCard = styled.div`
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;

  .item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .item-name {
    font-weight: 500;
    color: #1a1a1a;
    font-size: 14px;
  }

  .item-amount {
    font-weight: 600;
    color: #7c3aed;
    font-size: 14px;
  }

  .item-meta {
    font-size: 12px;
    color: #6b7280;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }
`

const MOBILE_BREAKPOINT = 768

const TableWrapper = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`

const MobileCardList = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    /* 加上 top padding，避免第一張卡片貼齊容器頂端、視覺上重疊邊界 */
    padding: 16px 16px 8px;
  }
`

const MobilePaginationWrap = styled.div`
  display: none;
  padding: 16px;
  justify-content: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
  }
`

const typeLabels = {
  customer_checkout: { label: '客戶結帳', color: 'green' },
  employee_purchase: { label: '員工自購', color: 'blue' },
  public_use: { label: '公用', color: 'orange' },
  gift: { label: '贈品', color: 'purple' }
}

const InventoryUsagePage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { hasFeature } = useMembership()
  const { can } = usePermission()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const canViewFinance = can('finance:view')
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [summary, setSummary] = useState({
    totalUsageCount: 0,
    totalAmount: 0,
    totalCost: 0
  })
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [sortedInfo, setSortedInfo] = useState({})
  const [searchExpanded, setSearchExpanded] = useState(false)

  // 搜索表单状态
  const [type, setType] = useState(null)
  const [dateRange, setDateRange] = useState(null)
  const [productId, setProductId] = useState(null)
  const [customerId, setCustomerId] = useState(null)
  const [employeeId, setEmployeeId] = useState(null)
  const [products, setProducts] = useState([])
  const [employees, setEmployees] = useState([])

  // Drawer 狀態
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)

  useEffect(() => {
    if (!hasFeature('inventory')) return
    fetchEmployees()
    fetchProducts()
    fetchRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    sortedInfo.field,
    sortedInfo.order
  ])

  const fetchEmployees = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  const fetchProducts = async () => {
    if (!user?.businessId) return
    try {
      const response = await getInventoryProducts({
        page: 1,
        pageSize: 1000
      })
      const productsData =
        response.data?.data?.items || response.data?.items || []
      setProducts(productsData)
    } catch (error) {
      console.error('獲取商品列表失敗:', error)
    }
  }

  const fetchRecords = async () => {
    if (!hasFeature('inventory')) return
    setLoading(true)
    try {
      // 只有當使用者明確點擊欄位排序（order 為 ascend/descend）時才送 sortField/sortOrder，
      // 否則一律走後端預設的 date desc，避免分頁切換時帶到殘留的 sortField（例如 totalAmount）
      // 造成資料不是依日期由新到舊排序。
      const explicitOrder =
        sortedInfo.order === 'ascend'
          ? 'asc'
          : sortedInfo.order === 'descend'
            ? 'desc'
            : null
      const sortField = explicitOrder ? sortedInfo.field || 'date' : 'date'
      const sortOrder = explicitOrder || 'desc'

      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        type: type || undefined,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        productId: productId || undefined,
        customerId: customerId || undefined,
        employeeId: employeeId || undefined,
        sortField,
        sortOrder
      }

      const response = await getInventoryUsage(params)

      // 處理 API 響應數據
      const data = response.data?.data || response.data || {}
      const recordsData = data.records || []
      const summaryData = data.summary || {}

      setRecords(recordsData)
      setSummary({
        totalUsageCount: summaryData.totalUsageCount || 0,
        totalAmount: summaryData.totalAmount || 0,
        totalCost: summaryData.totalCost || 0
      })

      // 處理分頁信息
      if (data.pagination) {
        setPagination({
          current: data.pagination.page || pagination.current,
          pageSize: data.pagination.pageSize || pagination.pageSize,
          total: data.total || data.pagination.total || recordsData.length
        })
      } else {
        setPagination({
          ...pagination,
          total: data.total || recordsData.length
        })
      }
    } catch (error) {
      console.error('獲取庫存使用記錄失敗:', error)
      message.error('獲取庫存使用記錄失敗，請稍後再試')
      setRecords([])
      setSummary({
        totalUsageCount: 0,
        totalAmount: 0,
        totalCost: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 })
    fetchRecords()
  }

  const handleReset = () => {
    setType(null)
    setDateRange(null)
    setProductId(null)
    setCustomerId(null)
    setEmployeeId(null)
    setPagination({ ...pagination, current: 1 })
    setTimeout(() => {
      fetchRecords()
    }, 100)
  }

  const handleTableChange = (paginationConfig, filters, sorter) => {
    // sorter 可能是 SorterResult 也可能是陣列；只有 order 真的是 ascend/descend
    // 才視為使用者明確排序，否則完全清空 sortedInfo，避免殘留 field 造成下一次
    // fetch 時誤用（例如把 sortField 帶成 totalAmount，造成日期錯亂）。
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter
    if (activeSorter && (activeSorter.order === 'ascend' || activeSorter.order === 'descend')) {
      setSortedInfo({
        field: activeSorter.field,
        order: activeSorter.order
      })
    } else {
      setSortedInfo({})
    }
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    })
  }

  // 打開詳情 Drawer
  const handleViewDetail = async (record) => {
    setSelectedRecord(record)
    setDetailDrawerVisible(true)
  }

  // 取消/退回使用記錄
  const handleCancelUsage = async (usageId) => {
    try {
      setLoading(true)
      await cancelInventoryUsage(usageId, {
        reason: '手動取消',
        note: '管理員手動取消使用記錄'
      })
      message.success('使用記錄已取消')
      setDetailDrawerVisible(false)
      fetchRecords()
    } catch (error) {
      console.error('取消使用記錄失敗:', error)
      message.error(
        error.response?.data?.message || '取消使用記錄失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: '項次',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      render: (_, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1
    },
    {
      title: '使用類型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        const typeInfo = typeLabels[type] || { label: type, color: 'default' }
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
      }
    },
    {
      title: '日期時間',
      dataIndex: 'date',
      key: 'date',
      width: 180,
      sorter: true,
      sortOrder: sortedInfo.field === 'date' && sortedInfo.order,
      render: (date, record) => {
        if (!date) return '-'
        const dateStr = dayjs(date).format('YYYY-MM-DD')
        const timeStr = record.time || ''
        return (
          <div>
            <div>{dateStr}</div>
            {timeStr && (
              <div style={{ fontSize: 12, color: '#999' }}>{timeStr}</div>
            )}
          </div>
        )
      }
    },
    {
      title: '客戶/員工',
      key: 'customer',
      width: 150,
      render: (_, record) => {
        if (record.customerName) {
          return (
            <div>
              <div>{record.customerName}</div>
              {record.customerPhone && (
                <div style={{ fontSize: 12, color: '#999' }}>
                  {record.customerPhone}
                </div>
              )}
            </div>
          )
        } else if (record.employeeName) {
          return record.employeeName
        }
        return '-'
      }
    },
    {
      title: '商品項目',
      key: 'items',
      width: 200,
      render: (_, record) => {
        if (!record.items || record.items.length === 0) return '-'
        const itemCount = record.items.length
        const firstItem = record.items[0]
        return (
          <div>
            <div>{firstItem.productName || '商品'}</div>
            {itemCount > 1 && (
              <div style={{ fontSize: 12, color: '#999' }}>
                等 {itemCount} 項商品
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: '銷售總金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      sorter: true,
      sortOrder: sortedInfo.field === 'totalAmount' && sortedInfo.order,
      render: (amount) => {
        if (amount === null || amount === undefined || amount === 0) return '-'
        return `NT$ ${formatMoney(amount)}`
      }
    },
    // 只有有 finance:view 权限才显示总成本列
    ...(canViewFinance
      ? [
          {
            title: '總成本',
            dataIndex: 'totalCost',
            key: 'totalCost',
            width: 120,
            align: 'right',
            sorter: true,
            sortOrder: sortedInfo.field === 'totalCost' && sortedInfo.order,
            render: (cost) => (cost ? `NT$ ${formatMoney(cost)}` : '-')
          }
        ]
      : []),
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        if (status === 'cancelled') {
          return <Tag color='red'>已取消</Tag>
        }
        return <Tag color='green'>正常</Tag>
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            詳情
          </Button>
          {record.status !== 'cancelled' && (
            <Popconfirm
              title='確定要取消此使用記錄嗎？'
              description='取消後將恢復商品庫存'
              onConfirm={() => handleCancelUsage(record._id)}
              okText='確定'
              cancelText='取消'
            >
              <Button type='link' danger icon={<CloseCircleOutlined />}>
                取消
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  // 權限檢查
  if (!hasFeature('inventory')) {
    return (
      <PageContainer>
        <Empty description='此功能需要庫存管理權限' />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>庫存使用記錄</PageTitle>
        <ActionButtons>
          <PrimaryButton icon={<ReloadOutlined />} onClick={fetchRecords}>
            刷新
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <SearchCard>
        <Collapse
          activeKey={searchExpanded ? ['search'] : []}
          onChange={(keys) => setSearchExpanded(keys.includes('search'))}
          expandIcon={({ isActive }) =>
            isActive ? <UpOutlined /> : <DownOutlined />
          }
        >
          <Panel
            header={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                搜尋條件
              </span>
            }
            key='search'
          >
            <SearchForm>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={8}>
                  <div className='search-row'>
                    <label className='search-label'>使用類型</label>
                    <Select
                      value={type}
                      onChange={(value) => setType(value)}
                      placeholder='請選擇使用類型'
                      size='large'
                      allowClear
                    >
                      {Object.entries(typeLabels).map(([key, info]) => (
                        <Option key={key} value={key}>
                          {info.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className='search-row'>
                    <label className='search-label'>日期區間</label>
                    <RangePicker
                      value={dateRange}
                      onChange={(dates) => setDateRange(dates)}
                      format='YYYY-MM-DD'
                      style={{ width: '100%' }}
                      size='large'
                      allowClear
                      inputReadOnly={isMobile}
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className='search-row'>
                    <label className='search-label'>商品</label>
                    <Select
                      value={productId}
                      onChange={(value) => setProductId(value)}
                      placeholder='請選擇商品'
                      size='large'
                      allowClear
                      showSearch
                      optionFilterProp='children'
                    >
                      {products.map((product) => (
                        <Option
                          key={product._id || product.id}
                          value={product._id || product.id}
                        >
                          {product.name || product.title}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={8}>
                  <div className='search-row'>
                    <label className='search-label'>員工</label>
                    <Select
                      value={employeeId}
                      onChange={(value) => setEmployeeId(value)}
                      placeholder='請選擇員工'
                      size='large'
                      allowClear
                      showSearch
                      optionFilterProp='children'
                    >
                      {employees.map((emp) => (
                        <Option key={emp._id} value={emp._id}>
                          {emp.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              </Row>
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Space>
                  <Button onClick={handleReset}>重置</Button>
                  <PrimaryButton
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                  >
                    搜尋
                  </PrimaryButton>
                </Space>
              </div>
            </SearchForm>
          </Panel>
        </Collapse>
      </SearchCard>

      <SummaryCard>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={canViewFinance ? 8 : 12}>
            <Statistic
              title='總使用次數'
              value={summary.totalUsageCount}
              valueStyle={{ color: '#7c3aed' }}
            />
          </Col>
          <Col xs={24} sm={canViewFinance ? 8 : 12}>
            <Statistic
              title='銷售總金額'
              value={Math.round(Number(summary.totalAmount || 0) * 100) / 100}
              prefix='NT$'
              precision={2}
              valueStyle={{ color: '#7c3aed' }}
              formatter={(v) =>
                Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
              }
            />
          </Col>
          {canViewFinance && (
            <Col xs={24} sm={8}>
              <Statistic
                title='總成本'
                value={Math.round(Number(summary.totalCost || 0) * 100) / 100}
                prefix='NT$'
                precision={2}
                valueStyle={{ color: '#10b981' }}
                formatter={(v) =>
                  Number(v).toLocaleString('en-US', {
                    maximumFractionDigits: 2
                  })
                }
              />
            </Col>
          )}
        </Row>
      </SummaryCard>

      <TableCard>
        <Spin spinning={loading}>
          <TableWrapper>
            <Table
              columns={columns}
              dataSource={records}
              rowKey='_id'
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 筆記錄`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </TableWrapper>

          <MobileCardList>
            {records.length === 0 ? (
              <div style={{ padding: '24px 16px' }}>
                <Empty description='尚無使用記錄' />
              </div>
            ) : (
              records.map((record, index) => {
                const typeInfo = typeLabels[record.type] || {
                  label: record.type,
                  color: 'default'
                }
                const customerDisplay = record.customerName
                  ? record.customerPhone
                    ? `${record.customerName} / ${record.customerPhone}`
                    : record.customerName
                  : record.employeeName || '-'
                const itemSummary =
                  record.items?.length > 0
                    ? record.items.length > 1
                      ? `${record.items[0]?.productName || '商品'} 等 ${record.items.length} 項`
                      : record.items[0]?.productName || '商品'
                    : '-'
                const isCancelled = record.status === 'cancelled'
                return (
                  <MobileRecordCard
                    key={record._id}
                    dimmed={isCancelled}
                    title={customerDisplay}
                    subtitle={`#${
                      (pagination.current - 1) * pagination.pageSize + index + 1
                    }`}
                    extra={
                      <>
                        <Tag color={typeInfo.color} style={{ marginRight: 0 }}>
                          {typeInfo.label}
                        </Tag>
                        {isCancelled ? (
                          <Tag color='red' style={{ marginRight: 0 }}>
                            已取消
                          </Tag>
                        ) : (
                          <Tag color='green' style={{ marginRight: 0 }}>
                            正常
                          </Tag>
                        )}
                      </>
                    }
                    rows={[
                      {
                        label: '日期時間',
                        value: record.date
                          ? dayjs(record.date).format('YYYY-MM-DD') +
                            (record.time ? ` ${record.time}` : '')
                          : '-'
                      },
                      { label: '商品項目', value: itemSummary },
                      {
                        label: '銷售總金額',
                        value:
                          record.totalAmount != null &&
                          record.totalAmount !== 0
                            ? `NT$ ${formatMoney(record.totalAmount)}`
                            : '-',
                        valueStyle: { color: '#7c3aed', fontWeight: 600 }
                      },
                      {
                        label: '總成本',
                        hidden: !canViewFinance,
                        value: record.totalCost
                          ? `NT$ ${formatMoney(record.totalCost)}`
                          : '-'
                      }
                    ]}
                    actions={
                      <>
                        <Button
                          type='primary'
                          size='small'
                          icon={<EyeOutlined />}
                          onClick={() => handleViewDetail(record)}
                        >
                          詳情
                        </Button>
                        {!isCancelled && (
                          <Popconfirm
                            title='確定要取消此使用記錄嗎？'
                            description='取消後將恢復商品庫存'
                            onConfirm={() => handleCancelUsage(record._id)}
                            okText='確定'
                            cancelText='取消'
                          >
                            <Button
                              type='primary'
                              danger
                              size='small'
                              icon={<CloseCircleOutlined />}
                            >
                              取消
                            </Button>
                          </Popconfirm>
                        )}
                      </>
                    }
                  />
                )
              })
            )}
          </MobileCardList>

          <MobilePaginationWrap>
            {records.length > 0 && pagination.total > 0 && (
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={(page, pageSize) =>
                  handleTableChange(
                    {
                      current: page,
                      pageSize: pageSize || pagination.pageSize
                    },
                    {},
                    {}
                  )
                }
                showSizeChanger={false}
                size='small'
                showTotal={(total) => `共 ${total} 筆記錄`}
              />
            )}
          </MobilePaginationWrap>
        </Spin>
      </TableCard>

      {/* 詳情 Drawer */}
      <StyledDrawer
        title='使用記錄詳情'
        placement='right'
        width={600}
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false)
          setSelectedRecord(null)
        }}
      >
        {selectedRecord && (
          <>
            <DetailSection>
              <div className='section-title'>
                <ShoppingOutlined style={{ marginRight: 8 }} />
                基本信息
              </div>
              <Descriptions column={1} bordered>
                <Descriptions.Item label='使用類型'>
                  {(() => {
                    const typeInfo = typeLabels[selectedRecord.type] || {
                      label: selectedRecord.type,
                      color: 'default'
                    }
                    return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label='日期'>
                  {selectedRecord.date
                    ? dayjs(selectedRecord.date).format('YYYY年MM月DD日')
                    : '-'}
                </Descriptions.Item>
                {selectedRecord.time && (
                  <Descriptions.Item label='時間'>
                    {selectedRecord.time}
                  </Descriptions.Item>
                )}
                {selectedRecord.customerName && (
                  <Descriptions.Item label='客戶名稱'>
                    {selectedRecord.customerName}
                  </Descriptions.Item>
                )}
                {selectedRecord.customerPhone && (
                  <Descriptions.Item label='客戶電話'>
                    {selectedRecord.customerPhone}
                  </Descriptions.Item>
                )}
                {selectedRecord.employeeName && (
                  <Descriptions.Item label='員工名稱'>
                    {selectedRecord.employeeName}
                  </Descriptions.Item>
                )}
                {selectedRecord.orderId && (
                  <Descriptions.Item label='訂單編號'>
                    {selectedRecord.orderId}
                  </Descriptions.Item>
                )}
                {selectedRecord.bookingId && (
                  <Descriptions.Item label='預約編號'>
                    {selectedRecord.bookingId}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label='銷售總金額'>
                  {selectedRecord.totalAmount
                    ? `NT$ ${formatMoney(selectedRecord.totalAmount)}`
                    : '-'}
                </Descriptions.Item>
                {canViewFinance && (
                  <Descriptions.Item label='總成本'>
                    {selectedRecord.totalCost
                      ? `NT$ ${formatMoney(selectedRecord.totalCost)}`
                      : '-'}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label='狀態'>
                  {selectedRecord.status === 'cancelled' ? (
                    <Tag color='red'>已取消</Tag>
                  ) : (
                    <Tag color='green'>正常</Tag>
                  )}
                </Descriptions.Item>
                {selectedRecord.note && (
                  <Descriptions.Item label='備註'>
                    {selectedRecord.note}
                  </Descriptions.Item>
                )}
                {selectedRecord.cancelReason && (
                  <Descriptions.Item label='取消原因'>
                    {selectedRecord.cancelReason}
                  </Descriptions.Item>
                )}
                {selectedRecord.cancelledAt && (
                  <Descriptions.Item label='取消時間'>
                    {dayjs(selectedRecord.cancelledAt).format(
                      'YYYY-MM-DD HH:mm'
                    )}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </DetailSection>

            <DetailSection>
              <div className='section-title'>
                <DollarOutlined style={{ marginRight: 8 }} />
                商品明細
              </div>
              {selectedRecord.items && selectedRecord.items.length > 0 ? (
                <ItemList>
                  {selectedRecord.items.map((item, index) => {
                    // 從目前的商品清單反查 capacity（資料庫只有快照 productName，
                    // 沒存 capacity；以即時 lookup 方式補上，舊紀錄也能顯示）
                    const matched = products.find(
                      (p) =>
                        String(p._id) === String(item.productId) ||
                        String(p.id) === String(item.productId)
                    )
                    const capacity = matched?.capacity
                    return (
                      <ItemCard key={index}>
                        <div className='item-header'>
                          <span className='item-name'>
                            {item.productName || '商品'}
                            {capacity && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  color: '#6B7280',
                                  fontWeight: 400,
                                  fontSize: 13
                                }}
                              >
                                ({capacity})
                              </span>
                            )}
                          </span>
                          {item.totalPrice > 0 && (
                            <span className='item-amount'>
                              NT$ {formatMoney(item.totalPrice)}
                            </span>
                          )}
                        </div>
                        <div className='item-meta'>
                          <span>數量：{item.quantity}</span>
                          {item.unitPrice > 0 && (
                            <span>
                              單價：NT$ {formatMoney(item.unitPrice)}
                            </span>
                          )}
                          {canViewFinance && item.cost !== undefined && (
                            <span>
                              成本：NT$ {formatMoney(item.cost)}
                            </span>
                          )}
                          {canViewFinance && item.totalCost !== undefined && (
                            <span>
                              總成本：NT$ {formatMoney(item.totalCost)}
                            </span>
                          )}
                        </div>
                      </ItemCard>
                    )
                  })}
                </ItemList>
              ) : (
                <Empty description='暫無商品明細' />
              )}
            </DetailSection>
          </>
        )}
      </StyledDrawer>
    </PageContainer>
  )
}

export default InventoryUsagePage
